import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation, Trans } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import {
  Box, Card, CardContent, Typography, Button, Stack, Alert,
  Skeleton, Switch, Collapse,
  Slider,
  IconButton, Tooltip, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, FormHelperText, TextField,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material'
import PhoneIcon from '@mui/icons-material/Phone'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { useNotificationSettings, useChannels, SETTINGS_KEY } from '../hooks/usePreferences'
import { updateSettings, addOrUpdateChannel, deleteChannel } from '../api/preferences'
import { usePhoneNumbers, useAddPhoneNumber, useVerifyPhoneNumber, useResendPhoneCode, useDeletePhoneNumber } from '../hooks/usePhoneNumbers'
import type { NotificationConfig, UserPhoneNumber, Preferences } from '../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function effectiveMax(value: number | undefined, fallback: number) {
  return value && value > 0 ? value : fallback
}

function useBorderColor(opacity: number = 0.12) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  return isDark ? `rgba(255,255,255,${opacity})` : `rgba(0,0,0,${opacity})`
}

// ── Confirmation dialog ────────────────────────────────────────────────────────

function ConfirmDialog({
  open, title, message, confirmLabel, onConfirm, onCancel, onExited,
}: {
  open: boolean
  title: string
  message: React.ReactNode
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
  onExited?: () => void
}) {
  const { t } = useTranslation()
  const borderColor = useBorderColor(0.1)
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
      TransitionProps={{ onExited }}
      slotProps={{ paper: { sx: { borderRadius: 3, border: `1px solid ${borderColor}` } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} variant="outlined" color="inherit">{t('common.cancel')}</Button>
        <Button onClick={onConfirm} variant="contained" color="error" autoFocus>
          {confirmLabel ?? t('common.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ── Notification channel defaults ─────────────────────────────────────────────

const DEFAULT_PHONE_CHANNEL: NotificationConfig = {
  type: 'PHONE_CALL',
  priority: 1,
  address: '',
  ringDurationSeconds: 30,
  maxRetries: 3,
  retryIntervalMinutes: 5,
}

// ── Channel card ───────────────────────────────────────────────────────────────

function ChannelCard({
  config, isEditing, onStartEdit, onSave, onCancel, onRemove,
  saving, error, onDraftChange, verifiedPhoneNumbers, maxChannels, otherChannels,
}: {
  config: NotificationConfig
  isEditing: boolean
  onStartEdit: () => void
  onSave: (draft: NotificationConfig) => void
  onCancel: () => void
  onRemove: () => void
  saving: boolean
  error: string | null
  onDraftChange: () => void
  verifiedPhoneNumbers: UserPhoneNumber[]
  maxChannels: number
  otherChannels: NotificationConfig[]
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = useBorderColor()

  // Local draft state — re-initialised on mount (parent uses key prop to remount on edit toggle)
  const [draft, setDraft] = useState<NotificationConfig>(config)
  const [phoneTouched, setPhoneTouched] = useState(!!config.address)
  const [numDrafts, setNumDrafts] = useState<Record<string, string>>({})

  const update = (field: keyof NotificationConfig, value: unknown) => {
    setDraft((d) => ({ ...d, [field]: value }))
    onDraftChange()
  }

  const numericProps = (field: 'priority' | 'maxRetries' | 'retryIntervalMinutes', min: number, max: number) => ({
    value: numDrafts[field] ?? String(draft[field]),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setNumDrafts((d) => ({ ...d, [field]: e.target.value }))
      onDraftChange()
    },
    onBlur: () => {
      const parsed = parseInt(numDrafts[field] ?? '')
      const cur = draft[field] as number
      update(field, isNaN(parsed) ? cur : Math.min(max, Math.max(min, parsed)))
      setNumDrafts((d) => { const n = { ...d }; delete n[field]; return n })
    },
  })

  const selectedPhone = verifiedPhoneNumbers.find((p) => p.number === config.address)
  const isDuplicate = !!draft.address && otherChannels.some(
    (c) => c.priority === draft.priority && c.address === draft.address
  )
  const isDirty =
    draft.address !== config.address ||
    draft.priority !== config.priority ||
    draft.ringDurationSeconds !== config.ringDurationSeconds ||
    draft.maxRetries !== config.maxRetries ||
    draft.retryIntervalMinutes !== config.retryIntervalMinutes
  const canSave = !!draft.address.trim() && !isDuplicate && isDirty
  const selectPlaceholderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'

  // ── View mode ────────────────────────────────────────────────────────────────
  if (!isEditing) {
    return (
      <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
          <PhoneIcon sx={{ color: config.address ? 'primary.main' : 'text.disabled', fontSize: 20, flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap>
              {config.address || t('preferences.channel.notConfigured')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {selectedPhone?.label ? `${selectedPhone.label} · ` : ''}
              {t('preferences.channel.details', {
                priority: config.priority,
                ring: config.ringDurationSeconds,
                retries: config.maxRetries,
                interval: config.retryIntervalMinutes,
              })}
            </Typography>
          </Box>
          <Tooltip title={t('preferences.channel.editTooltip')}>
            <IconButton size="small" onClick={onStartEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('preferences.channel.removeTooltip')}>
            <IconButton size="small" color="error" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    )
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, p: 2 }}>
      <Stack spacing={2}>
        <FormControl fullWidth size="small" error={phoneTouched && !draft.address}>
          <InputLabel shrink>{t('preferences.channel.phone')}</InputLabel>
          <Select
            value={draft.address}
            label={t('preferences.channel.phone')}
            notched
            onChange={(e) => { setPhoneTouched(true); update('address', e.target.value as string) }}
            onClose={() => setPhoneTouched(true)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <em style={{ color: selectPlaceholderColor }}>{t('preferences.channel.selectNumber')}</em>
            </MenuItem>
            {verifiedPhoneNumbers.map((p) => (
              <MenuItem key={p.number} value={p.number}>
                {p.number}{p.label ? ` (${p.label})` : ''}
              </MenuItem>
            ))}
          </Select>
          {phoneTouched && !draft.address && (
            <FormHelperText>{t('preferences.channel.required')}</FormHelperText>
          )}
        </FormControl>

        <TextField
          size="small" label={t('preferences.channel.priority')} type="number"
          {...numericProps('priority', 1, maxChannels)}
          fullWidth
          slotProps={{ htmlInput: { min: 1, max: maxChannels }, inputLabel: { shrink: true } }}
        />

        <Box data-no-swipe>
          <Typography variant="body2" gutterBottom>
            {t('preferences.channel.ringDuration', { seconds: draft.ringDurationSeconds })}
          </Typography>
          <Slider
            value={draft.ringDurationSeconds}
            onChange={(_, v) => update('ringDurationSeconds', v as number)}
            min={15} max={60} step={5}
            marks={[{ value: 15, label: t('preferences.channel.ringMin') }, { value: 60, label: t('preferences.channel.ringMax') }]}
          />
        </Box>

        <Stack direction="row" spacing={2}>
          <TextField
            size="small" label={t('preferences.channel.maxRetries')} type="number"
            {...numericProps('maxRetries', 1, 5)}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { min: 1, max: 5 }, inputLabel: { shrink: true } }}
          />
          <TextField
            size="small" label={t('preferences.channel.retryInterval')} type="number"
            {...numericProps('retryIntervalMinutes', 1, 10)}
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { min: 1, max: 10 }, inputLabel: { shrink: true } }}
          />
        </Stack>

        {isDuplicate && (
          <Typography variant="body2" color="warning.main">
            {t('preferences.duplicatePriority', { priority: draft.priority })}
          </Typography>
        )}
        {error && <Typography variant="body2" color="error.main">{error}</Typography>}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained" size="small"
            startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
            onClick={() => onSave(draft)}
            disabled={saving || !canSave}
          >
            {saving ? t('preferences.channel.saving') : t('preferences.channel.save')}
          </Button>
          <Button variant="outlined" size="small" color="inherit" onClick={onCancel} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

// ── Phone numbers section ─────────────────────────────────────────────────────

const RESEND_COOLDOWN = 60 // seconds

const E164_REGEX = /^\+[1-9]\d{6,14}$/

interface PendingPhone {
  id: number
  number: string
  label: string
  codeSent: boolean
  code: string
  sentAt?: string // ISO string — set when code is sent, used for resend cooldown
  lastAttemptedCode?: string // prevents re-submitting same wrong code without changing it
  numberError?: string // client-side format error shown after send attempt
  addError?: string
  codeInvalid?: boolean  // wrong code from verify (key-based so language switches instantly)
  verifyError?: string   // raw API error from resend
}

function PhoneNumbersCard() {
  const { t } = useTranslation()
  const borderColor = useBorderColor(0.1)
  const borderColorPending = useBorderColor(0.08)

  const { data: settings } = useNotificationSettings()
  const { data: phoneNumbers, isLoading } = usePhoneNumbers()
  const { mutate: add, isPending: adding } = useAddPhoneNumber()
  const { mutate: verify, isPending: verifying } = useVerifyPhoneNumber()
  const { mutate: resend, isPending: resending } = useResendPhoneCode()
  const { mutate: remove } = useDeletePhoneNumber()

  // Tick every second to drive resend cooldown countdown
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  const maxPhoneNumbers = effectiveMax(settings?.maxPhoneNumbers, 2)
  const verifiedPhoneNumbers = (phoneNumbers ?? []).filter((p) => p.verified)

  const nextId = useRef(1)
  const [pendingPhones, setPendingPhones] = useState<PendingPhone[]>([])
  // Auto-show code input for any unverified number that still has a valid verification code
  const initializedRef = useRef(false)
  useEffect(() => {
    if (initializedRef.current || !phoneNumbers || phoneNumbers.length === 0) return
    const unverifiedWithCode = phoneNumbers.filter(
      (p) => !p.verified && p.pendingVerification && new Date(p.pendingVerification.expiry) > new Date()
    )
    if (unverifiedWithCode.length > 0) {
      initializedRef.current = true
      setPendingPhones(
        unverifiedWithCode.map((p) => ({
          id: nextId.current++,
          number: p.number,
          label: p.label ?? '',
          labelTouched: false,
          codeSent: true,
          code: '',
          sentAt: p.pendingVerification?.sentAt,
        }))
      )
    }
  }, [phoneNumbers])

  const updatePending = (id: number, patch: Partial<PendingPhone>) =>
    setPendingPhones((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  const removePending = (id: number) =>
    setPendingPhones((prev) => prev.filter((p) => p.id !== id))

  const isTaken = (num: string) => (phoneNumbers ?? []).some((p) => p.number === num.trim())
  const isLabelTaken = (label: string) =>
    !!label.trim() && (phoneNumbers ?? []).some((p) => p.label === label.trim())

  const extractApiError = (err: unknown): string => {
    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
    return msg ?? t('common.unexpectedError')
  }

  const handleSendCode = (pending: PendingPhone) => {
    const trimmedNumber = pending.number.trim()
    const trimmedLabel = pending.label.trim()
    if (!trimmedNumber || isTaken(trimmedNumber)) return
    if (!E164_REGEX.test(trimmedNumber)) {
      updatePending(pending.id, { numberError: t('preferences.invalidFormat') })
      return
    }
    updatePending(pending.id, { number: trimmedNumber, label: trimmedLabel, numberError: undefined })
    add(
      { number: trimmedNumber, label: trimmedLabel || t('common.main') },
      {
        onSuccess: () => updatePending(pending.id, { codeSent: true, code: '', sentAt: new Date().toISOString(), addError: undefined }),
        onError: (err) => updatePending(pending.id, { addError: extractApiError(err) }),
      }
    )
  }

  const handleResendCode = (pending: PendingPhone) => {
    resend(pending.number.trim(), {
      onSuccess: () => updatePending(pending.id, { code: '', sentAt: new Date().toISOString() }),
      onError: (err) => updatePending(pending.id, { verifyError: extractApiError(err) }),
    })
  }

  const handleResendFromList = (p: UserPhoneNumber) => {
    resend(p.number, {
      onSuccess: () => {
        if (!pendingPhones.some((pp) => pp.number === p.number)) {
          setPendingPhones((prev) => [
            ...prev,
            { id: nextId.current++, number: p.number, label: p.label ?? '', codeSent: true, code: '', sentAt: new Date().toISOString() },
          ])
        }
      },
    })
  }

  const handleVerifyCode = (pending: PendingPhone) => {
    verify(
      { number: pending.number.trim(), code: pending.code.trim() },
      {
        onSuccess: () => removePending(pending.id),
        onError: () => updatePending(pending.id, { codeInvalid: true, lastAttemptedCode: pending.code.trim() }),
      }
    )
  }

  const existingCount = phoneNumbers?.length ?? 0
  const atPhoneLimit = existingCount >= maxPhoneNumbers
  const hasPendingForm = pendingPhones.length > 0
  const addButtonEnabled = !atPhoneLimit && !hasPendingForm && (existingCount === 0 || verifiedPhoneNumbers.length > 0)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [numberToRemove, setNumberToRemove] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const requestRemove = (number: string) => { setNumberToRemove(number); setConfirmOpen(true) }
  const confirmRemove = () => {
    if (numberToRemove) {
      const number = numberToRemove
      setDeleteError(null)
      remove(number, {
        onSuccess: () => {
          setPendingPhones((prev) => prev.filter((p) => p.number !== number))
        },
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          setDeleteError(msg ?? t('preferences.channelConflict'))
        },
      })
    }
    setConfirmOpen(false)
  }

  const phoneToRemoveObj = (phoneNumbers ?? []).find((p) => p.number === numberToRemove)

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        title={t('preferences.deletePhoneTitle')}
        message={
          <Trans
            i18nKey="preferences.deletePhoneMessage"
            values={{ number: numberToRemove ?? '', label: phoneToRemoveObj?.label ?? '' }}
            components={{ strong: <strong style={{ fontFamily: 'monospace' }} /> }}
          />
        }
        confirmLabel={t('preferences.delete')}
        onConfirm={confirmRemove}
        onCancel={() => setConfirmOpen(false)}
        onExited={() => setNumberToRemove(null)}
      />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography variant="h6" fontWeight={700}>{t('preferences.phoneNumbers')}</Typography>
            {settings && (
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('preferences.countOf', { count: existingCount, max: maxPhoneNumbers })}
              </Typography>
            )}
          </Stack>


          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}

          {/* Existing numbers */}
          {isLoading ? (
            <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
          ) : (phoneNumbers ?? []).length > 0 ? (
            <Stack spacing={1} mb={2}>
              {(phoneNumbers ?? []).map((p) => {
                const activePending = pendingPhones.find((pp) => pp.number === p.number && pp.codeSent)
                return (
                  <Box key={p.number} sx={{ border: `1px solid ${borderColor}`, borderRadius: 2, overflow: 'hidden' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                      <PhoneIcon sx={{ color: p.verified ? 'success.main' : 'text.disabled', fontSize: 20, flexShrink: 0 }} />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap>
                          {p.number}
                        </Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {p.label && (
                            <Typography variant="caption" color="text.secondary" noWrap>{p.label}</Typography>
                          )}
                          {p.label && !activePending && <Typography variant="caption" color="text.disabled">·</Typography>}
                          {p.verified ? (
                            <Typography variant="caption" color="success.main">✓ {t('preferences.verified')}</Typography>
                          ) : !activePending && (
                            p.pendingVerification && new Date(p.pendingVerification.expiry) > new Date() ? (
                              <Typography variant="caption" color="warning.main">{t('preferences.pending')}</Typography>
                            ) : (
                              <Button size="small" variant="text" color="warning"
                                sx={{ p: 0, minWidth: 0, fontSize: '0.75rem', lineHeight: 1.5 }}
                                disabled={resending} onClick={() => handleResendFromList(p)}>
                                {t('preferences.sendAgain')}
                              </Button>
                            )
                          )}
                        </Stack>
                      </Box>
                      <Tooltip title={t('preferences.removeTooltip')}>
                        <IconButton size="small" color="error" onClick={() => requestRemove(p.number)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {/* Inline verification — smooth expand when code has been sent */}
                    <Collapse in={!!activePending} unmountOnExit>
                      {activePending && (
                        <Box sx={{ px: 2, pt: 0.5, pb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                            <Box>
                              <TextField
                                size="small"
                                label={t('preferences.verificationCode')}
                                value={activePending.code}
                                onChange={(e) => updatePending(activePending.id, { code: e.target.value, codeInvalid: undefined, verifyError: undefined, lastAttemptedCode: undefined })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && activePending.code.trim() && !verifying && activePending.code.trim() !== activePending.lastAttemptedCode) {
                                    handleVerifyCode(activePending)
                                  }
                                }}
                                placeholder={t('preferences.codePlaceholder')}
                                autoFocus
                                error={!!activePending.codeInvalid || !!activePending.verifyError}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 6 } }}
                                sx={{ width: 160 }}
                              />
                              {(activePending.codeInvalid || activePending.verifyError) && (
                                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25, px: 0.25 }}>
                                  {activePending.codeInvalid ? t('preferences.invalidCode') : activePending.verifyError}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              size="small" variant="contained"
                              onClick={() => handleVerifyCode(activePending)}
                              disabled={!activePending.code.trim() || verifying || activePending.code.trim() === activePending.lastAttemptedCode}
                              sx={{ height: 40, flexShrink: 0 }}
                            >
                              {verifying ? t('preferences.verifying') : t('preferences.verify')}
                            </Button>
                            {(() => {
                              const secondsSinceSent = activePending.sentAt
                                ? Math.floor((now - new Date(activePending.sentAt).getTime()) / 1000)
                                : RESEND_COOLDOWN
                              const cooldownLeft = Math.min(RESEND_COOLDOWN, Math.max(0, RESEND_COOLDOWN - secondsSinceSent))
                              return (
                                <Button
                                  size="small" variant="outlined" color="inherit"
                                  disabled={cooldownLeft > 0 || resending}
                                  onClick={() => handleResendCode(activePending)}
                                  sx={{ height: 40, flexShrink: 0, color: cooldownLeft > 0 ? 'text.disabled' : 'text.secondary' }}
                                >
                                  {cooldownLeft > 0 ? t('preferences.resendIn', { seconds: cooldownLeft }) : t('preferences.resend')}
                                </Button>
                              )
                            })()}
                          </Stack>
                        </Box>
                      )}
                    </Collapse>
                  </Box>
                )
              })}
            </Stack>
          ) : !pendingPhones.length && (
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2, fontStyle: 'italic' }}>
              {t('preferences.noPhones')}
            </Typography>
          )}

          {/* Add-new flow — only for entries not yet in the phone list */}
          {pendingPhones.filter((p) => !p.codeSent).map((pending) => (
            <Box key={pending.id} sx={{ border: `1px solid ${borderColorPending}`, borderRadius: 2, p: 2, mb: 2 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                  <TextField
                    size="small" label={t('preferences.phoneLabel')} value={pending.number}
                    onChange={(e) => updatePending(pending.id, { number: e.target.value, numberError: undefined })}
                    onBlur={() => {
                      const trimmed = pending.number.trim()
                      if (trimmed && !isTaken(trimmed) && !E164_REGEX.test(trimmed)) {
                        updatePending(pending.id, { numberError: t('preferences.invalidFormat') })
                      }
                    }}
                    placeholder={t('preferences.phonePlaceholder')}
                    error={isTaken(pending.number) || !!pending.numberError}
                    helperText={isTaken(pending.number) ? t('preferences.alreadyRegistered') : pending.numberError}
                    sx={{ flex: 1, width: { xs: '100%' } }}
                    slotProps={{ htmlInput: { maxLength: 16 }, inputLabel: { shrink: true } }}
                  />
                  <TextField
                    size="small" label={t('preferences.label')} value={pending.label}
                    onChange={(e) => updatePending(pending.id, { label: e.target.value.slice(0, 20) })}
                    placeholder={t('preferences.labelPlaceholder')}
                    error={isLabelTaken(pending.label)}
                    helperText={isLabelTaken(pending.label) ? t('preferences.labelTaken') : t('preferences.charCount', { count: pending.label.length })}
                    sx={{ width: { xs: '100%', sm: 120 } }}
                    slotProps={{ htmlInput: { maxLength: 20 }, inputLabel: { shrink: true } }}
                  />
                  {/* Buttons: inline on desktop, own row on mobile */}
                  <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' }, height: 40, alignItems: 'center' }}>
                    <Button
                      variant="contained" size="small"
                      startIcon={adding ? <CircularProgress size={14} /> : undefined}
                      onClick={() => handleSendCode(pending)}
                      disabled={!pending.number.trim() || !pending.label.trim() || adding || isTaken(pending.number) || isLabelTaken(pending.label)}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('preferences.sendCode')}
                    </Button>
                    <Button variant="outlined" size="small" color="inherit" onClick={() => removePending(pending.id)}>
                      {t('common.cancel')}
                    </Button>
                  </Stack>
                </Stack>
                {/* Buttons row — mobile only */}
                <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                  <Button
                    variant="contained" size="small"
                    startIcon={adding ? <CircularProgress size={14} /> : undefined}
                    onClick={() => handleSendCode(pending)}
                    disabled={!pending.number.trim() || !pending.label.trim() || adding || isTaken(pending.number) || isLabelTaken(pending.label)}
                  >
                    {t('preferences.sendCode')}
                  </Button>
                  <Button variant="outlined" size="small" color="inherit" onClick={() => removePending(pending.id)}>
                    {t('common.cancel')}
                  </Button>
                </Stack>
                {pending.addError && (
                  <Typography variant="caption" color="error.main">
                    {pending.addError}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}

          {/* Add button + limits */}
          {!atPhoneLimit && (
            <Stack spacing={0.5}>
              <Box>
                <Button
                  startIcon={<AddIcon />} variant="outlined" size="small"
                  disabled={!addButtonEnabled}
                  onClick={() =>
                    setPendingPhones([{ id: nextId.current++, number: '', label: '', codeSent: false, code: '' }])
                  }
                >
                  {t('preferences.addPhone')}
                </Button>
              </Box>
              {!hasPendingForm && existingCount > 0 && verifiedPhoneNumbers.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('preferences.verifyFirst')}
                </Typography>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>
    </>
  )
}

// ── Channels + settings card ──────────────────────────────────────────────────

function ChannelsCard() {
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const { data: settings, isLoading: settingsLoading } = useNotificationSettings()
  const { data: channelsData, isLoading: channelsLoading, isError } = useChannels()
  const { data: phoneNumbers } = usePhoneNumbers()

  const isLoading = settingsLoading || channelsLoading
  const verifiedPhoneNumbers = (phoneNumbers ?? []).filter((p) => p.verified)
  const maxChannels = effectiveMax(settings?.maxNotificationChannels, 5)

  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [togglingEnabled, setTogglingEnabled] = useState(false)
  const [phoneChannels, setPhoneChannels] = useState<NotificationConfig[]>([])
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [channelSaving, setChannelSaving] = useState(false)
  const [channelErr, setChannelErr] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  // Ref so the useEffect can read editingIdx without it being a dependency
  const editingIdxRef = useRef<number | null>(null)
  editingIdxRef.current = editingIdx

  useEffect(() => {
    if (settings && channelsData !== undefined) {
      const channels = channelsData.filter((c) => c.type === 'PHONE_CALL')
      setNotificationsEnabled(settings.notificationsEnabled ?? true)
      // Don't overwrite local state while user is editing a channel
      if (editingIdxRef.current === null) {
        setPhoneChannels(channels)
      }
    }
  }, [settings, channelsData])

  const handleToggle = async (newValue: boolean) => {
    setTogglingEnabled(true)
    try {
      await updateSettings({ notificationsEnabled: newValue })
      setNotificationsEnabled(newValue)
    } finally {
      setTogglingEnabled(false)
    }
  }

  const addChannel = () => {
    if (editingIdx !== null) return
    const used = new Set(phoneChannels.map((c) => c.priority))
    let next = 1
    while (used.has(next)) next++
    const newCh: NotificationConfig = { ...DEFAULT_PHONE_CHANNEL, priority: next, address: '' }
    setPhoneChannels((prev) => [...prev, newCh])
    setEditingIdx(phoneChannels.length)
    setChannelErr(null)
  }

  const handleStartEdit = (idx: number) => {
    setEditingIdx(idx)
    setChannelErr(null)
  }

  const handleCancelEdit = () => {
    // New unsaved channel — remove it from the list
    if (editingIdx !== null && !phoneChannels[editingIdx]?.channelId) {
      setPhoneChannels((prev) => prev.filter((_, i) => i !== editingIdx))
    }
    setEditingIdx(null)
    setChannelErr(null)
  }

  const handleSaveChannel = async (draft: NotificationConfig) => {
    setChannelSaving(true)
    setChannelErr(null)
    try {
      const updated = await addOrUpdateChannel(draft)
      setPhoneChannels(updated)
      setEditingIdx(null)
      // Patch the cache directly — avoids a background refetch that would race with
      // any edit the user starts immediately after saving.
      queryClient.setQueryData(SETTINGS_KEY, (old: Preferences | undefined) =>
        old ? { ...old, channels: updated } : old
      )
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setChannelErr(msg ?? t('common.unexpectedError'))
    } finally {
      setChannelSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDeleteId) return
    const id = confirmDeleteId
    setConfirmDeleteId(null)
    setDeleteErr(null)
    try {
      await deleteChannel(id)
      setPhoneChannels((prev) => prev.filter((c) => c.channelId !== id))
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setDeleteErr(msg ?? t('preferences.channelConflict'))
    }
  }

  const channelToDelete = phoneChannels.find((c) => c.channelId === confirmDeleteId)

  if (isError) {
    return <Alert severity="error">{t('preferences.loadError')}</Alert>
  }

  return (
    <>
      <ConfirmDialog
        open={!!confirmDeleteId}
        title={t('preferences.deleteChannelTitle')}
        message={
          <Trans
            i18nKey="preferences.deleteChannelMessage"
            values={{ address: channelToDelete?.address ?? '' }}
            components={{ strong: <strong style={{ fontFamily: 'monospace' }} /> }}
          />
        }
        confirmLabel={t('preferences.delete')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteId(null)}
        onExited={() => setConfirmDeleteId(null)}
      />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight={700}>{t('preferences.channels')}</Typography>
            {settings && (
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {t('preferences.countOf', { count: phoneChannels.length, max: maxChannels })}
              </Typography>
            )}
          </Stack>

          {/* Notifications toggle — fires immediately */}
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
            {notificationsEnabled
              ? <NotificationsActiveIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
              : <NotificationsOffIcon sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }} />
            }
            <Typography variant="body2" flex={1}>
              {notificationsEnabled ? t('preferences.notifEnabled') : t('preferences.notifDisabled')}
            </Typography>
            {isLoading ? (
              <Skeleton variant="rounded" width={52} height={28} />
            ) : (
              <Switch
                checked={notificationsEnabled}
                onChange={(e) => handleToggle(e.target.checked)}
                disabled={togglingEnabled}
                color="primary"
                size="small"
              />
            )}
          </Stack>

          {deleteErr && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteErr(null)}>{deleteErr}</Alert>
          )}

          {/* Channels list */}
          {isLoading ? (
            <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
          ) : phoneChannels.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ mb: 2, fontStyle: 'italic' }}>
              {verifiedPhoneNumbers.length === 0
                ? t('preferences.verifyPhoneFirst')
                : t('preferences.noChannels')}
            </Typography>
          ) : (
            <Stack spacing={1} mb={2}>
              {phoneChannels.map((ch, idx) => (
                <ChannelCard
                  key={editingIdx === idx ? `edit-${ch.channelId ?? idx}` : `view-${ch.channelId ?? idx}`}
                  config={ch}
                  isEditing={editingIdx === idx}
                  onStartEdit={() => handleStartEdit(idx)}
                  onSave={handleSaveChannel}
                  onCancel={handleCancelEdit}
                  onRemove={() => ch.channelId ? setConfirmDeleteId(ch.channelId) : handleCancelEdit()}
                  saving={channelSaving}
                  error={editingIdx === idx ? channelErr : null}
                  onDraftChange={() => setChannelErr(null)}
                  verifiedPhoneNumbers={verifiedPhoneNumbers}
                  maxChannels={maxChannels}
                  otherChannels={phoneChannels.filter((_, i) => i !== idx)}
                />
              ))}
            </Stack>
          )}

          {/* Add channel button */}
          {!isLoading && (
            <>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={addChannel}
                disabled={editingIdx !== null || phoneChannels.length >= maxChannels || verifiedPhoneNumbers.length === 0}
              >
                {t('preferences.addChannel')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PreferencesPage() {
  return (
    <Box sx={{ maxWidth: 680 }}>
      <PhoneNumbersCard />
      <ChannelsCard />
    </Box>
  )
}
